import {
  Collapse,
  Fade,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import RefreshIcon from '@material-ui/icons/Refresh'
import { ReactNode, useState } from 'react'

import { EmptyState } from '../../components/EmptyState'
import { ResourceColumn } from './resourceColumns'
import { t } from './shared'
import useStyles from './styles/NotificationResourceTable.styles'
import { ResourceRow } from './types'

interface NotificationResourceTableProps {
  title: string
  rows: ResourceRow[]
  columns: ResourceColumn[]
  loading: boolean
  /** True once a load has finished without error, so empty means empty. */
  settled: boolean
  reloadData: () => void
  /** Per-row controls (action, delete). Rendered in the trailing cell. */
  renderActions?: (row: ResourceRow) => ReactNode
  /** Replaces the empty-state text when the list is empty for a known reason. */
  emptyMessage?: string
}

/**
 * An admin resource list in the portal's list shape (§2).
 *
 * §2 asks for one column constant shared by header, row and skeleton so the
 * three cannot drift. A single `const COLUMNS` string cannot serve this table —
 * it is generic over six resource types with different column sets — so the
 * `columns` array plays that role instead: the grid template is derived from it
 * once, and header, rows and skeleton all consume that one derivation. That is
 * the rule's intent (one source, no drift) rather than its letter, and it is
 * stronger than a string constant because a column cannot be added without a
 * width.
 */
export default function NotificationResourceTable({
  title,
  rows,
  columns,
  // `loading` is deliberately NOT destructured. It stays on the props interface
  // because both call sites pass it, but this component renders nothing from it:
  // the spinner belongs to ResourcePanel/DeliveryList, and emptiness is decided by
  // `settled`. Destructuring it unused is a no-unused-vars error, and react-scripts
  // escalates that to a failed production build.
  settled,
  reloadData,
  renderActions,
  emptyMessage
}: NotificationResourceTableProps) {
  const classes = useStyles()
  const theme = useTheme()
  // useMediaQuery rather than <Hidden>: §2 names both, and SupportTickets --
  // the screen this list is modelled on -- uses the query. It also renders one
  // layout instead of mounting both and hiding one.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [expanded, setExpanded] = useState<string>()

  // The trailing track holds the expander and any row actions.
  const gridTemplateColumns = `${columns
    .map((column) => column.width)
    .join(' ')} 3fr`

  const rowKey = (row: ResourceRow, index: number): string =>
    String(row.id ?? row.key ?? `${title}-${index}`)

  return (
    <div>
      {!isMobile && (
        <div className={classes.tableHead} style={{ gridTemplateColumns }}>
          {columns.map((column) => (
            <div key={column.label}>
              <Typography variant="body1">{t(column.label)}</Typography>
            </div>
          ))}
          <div className={classes.refreshButtonContainer}>
            <IconButton onClick={reloadData} aria-label={t('Refresh')}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
      {/* Refresh still has to exist on mobile; only its home changes. */}
      {isMobile && (
        <div className={classes.mobileToolbar}>
          <IconButton onClick={reloadData} aria-label={t('Refresh')}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* The shared EmptyState, not a bare <img>: it carries the label as
          text for a screen reader and keeps the image decorative. SupportTickets
          inlines its own image, which predates the shared component. */}
      {settled && !rows.length && (
        <EmptyState label={title} message={emptyMessage} />
      )}

      {rows.map((row, index) => {
        const key = rowKey(row, index)
        const isExpanded = expanded === key
        return (
          <Fade in timeout={600} key={key}>
            <div>
              <div
                // The layout is the thing under test; the class name alone is a
                // hashed JSS string, so a test cannot assert on it.
                data-testid={isMobile ? 'resource-card' : 'resource-row'}
                className={isMobile ? classes.mobileCard : classes.tableRow}
                style={isMobile ? undefined : { gridTemplateColumns }}>
                {/* Both layouts read the same `columns` array. §2 asks for
                    duplicate LAYOUT and never duplicate behaviour; deriving
                    both from one source is the strongest form of that, and it
                    means a column cannot exist on one breakpoint only. On
                    mobile each cell gains its header as a label, because a
                    bare value with no column above it is unreadable. */}
                {columns.map((column) => (
                  <div key={column.label}>
                    {isMobile && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        className={classes.mobileLabel}>
                        {t(column.label)}
                      </Typography>
                    )}
                    {column.render(row)}
                  </div>
                ))}
                <div className={classes.actionsCell}>
                  {renderActions?.(row)}
                  {/* The full record stays reachable. Columns carry what an
                      operator scans for; fields that cannot occupy a cell — CEL
                      source, template bodies, settings JSON — live here, which
                      is the same view this panel showed before. Nothing was
                      removed from the screen, only demoted. */}
                  <IconButton
                    size="small"
                    aria-label={
                      isExpanded ? t('Hide details') : t('Show details')
                    }
                    aria-expanded={isExpanded}
                    onClick={() => setExpanded(isExpanded ? undefined : key)}>
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </div>
              </div>
              <Collapse in={isExpanded} unmountOnExit>
                <pre className={classes.detail}>
                  {JSON.stringify(row, null, 2)}
                </pre>
              </Collapse>
            </div>
          </Fade>
        )
      })}
    </div>
  )
}
